import CheckIcon from '@mui/icons-material/Check';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { CustomToolbarAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';

import {
  PositionDoneDialog,
  PositionDoneFormData,
} from './components/PositionDoneDialog';

export interface UsePositionDoneOptions {
  contract?: Contract;
  overrides?: {
    user?: User;
    date?: string;
    position?: Position;
  };
  onPositionDone?: (formData: PositionDoneFormData) => void;
}

/**
 * Hook that provides CustomToolbarAction[] for marking positions as done.
 * Opens a confirmation dialog with form fields before executing the action.
 *
 * @param options - Configuration options including onPositionDone callback
 * @returns Array of CustomToolbarAction for position done functionality
 */
export const usePositionDone = (options?: UsePositionDoneOptions) => {
  const { t } = useTranslation();
  const dialogs = useDialogs();
  const { contract, onPositionDone, overrides } = options ?? {};

  const actions = useMemo<CustomToolbarAction[]>(() => {
    return [
      {
        actionKey: 'markPositionDone',
        IconComponent: CheckIcon,
        title: t('CHECK_LIST_POSITIONS_TABLE.DONE'),
        tooltip: t('CHECK_LIST_POSITIONS_TABLE.DONE'),
        mode: 'Edit',
        handler: () => {
          const result = dialogs.open(PositionDoneDialog, {
            contract,
            overrides,
            onConfirm: (formData: PositionDoneFormData) => {
              onPositionDone?.(formData);
              dialogs.close(result.id);
            },
            onClose: () => {
              dialogs.close(result.id);
            },
          });
        },
      },
    ];
  }, [t, dialogs, contract, onPositionDone, overrides]);

  return actions;
};
